import { FC, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from '@zoopla-eng/zds-components/box';
import { Button } from '@zoopla-eng/zds-components/button';

import { ReassuranceText, StyledGeneralError } from '../AuthModal.styles';
import PreferencesFormData from './../interfaces/PreferencesFormData';
import useUpdatePreferences from '../hooks/useUpdatePreferences';
import { useLazyOptimizelyExperiment } from '@zoopla/optimizely';
import { useUser } from '@zoopla/redux-reducer-user';
import { trackAuthSegmentEvent } from '../utils/segmentTrack';
import { PreferencesProps } from './Preferences.types';
import useUpdateAboutMe from '../hooks/useUpdateAboutMe';
import { trackAboutMe, trackOptin, trackSubmit } from './Preferences.tracking';
import { AboutYou } from '../common/AboutYou';
import { EmailPreferencesToggle } from '../common/EmailPreferencesToggle';

const PreferencesForm: FC<PreferencesProps> = ({ close, actionAfterAuth }) => {
    const {
        control,
        handleSubmit,
        formState: { errors }
    } = useForm<PreferencesFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });
    const user = useUser();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { updatePreferences, generalError } = useUpdatePreferences();
    const { updateAboutMe, generalError: aboutMeGeneralError } =
        useUpdateAboutMe();
    const [isLoadingFlag, isFlagEnabled] = useLazyOptimizelyExperiment(
        'crme-2002-your-situation-reg-experiment'
    );

    const onFormSubmit: SubmitHandler<PreferencesFormData> = async (
        formData
    ) => {
        setIsSubmitting(true);

        const payload = await updatePreferences(formData);
        const { consent__insights__24, situation } = formData;

        const parsedConsent = consent__insights__24 === 'yes' ? true : false;

        if (!payload?.data?.updateUserConsents) {
            return;
        }

        if (consent__insights__24 !== undefined) {
            trackOptin(parsedConsent, actionAfterAuth?.eventLabel);

            if (parsedConsent) {
                trackAuthSegmentEvent('optin', actionAfterAuth?.segment, user);
            }
        }

        if (situation?.id) {
            await updateAboutMe(formData);
        }

        trackAboutMe(situation?.id);

        trackSubmit(actionAfterAuth?.eventLabel);

        setIsSubmitting(false);

        // passing the user to be able to see if they
        // are a new registration for confirmation screen
        close(user);
    };

    const onSubmit = handleSubmit(onFormSubmit);

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            data-testid="auth-modal-preferences-form"
        >
            <div role="alert">
                {(generalError || aboutMeGeneralError) && (
                    <Box pb="$S500">
                        <StyledGeneralError data-testid="general-error">
                            Sorry, something went wrong. Please try again.
                        </StyledGeneralError>
                    </Box>
                )}
            </div>

            {!isLoadingFlag && isFlagEnabled !== 'enabled' && (
                <Box pb="$M100" bpM100={{ pb: '$M300' }}>
                    <Controller
                        control={control}
                        name="situation"
                        render={({ field: { onChange, value, ref } }) => (
                            <AboutYou
                                label="What can Zoopla help you with? (optional)"
                                value={value}
                                onChange={onChange}
                                ref={ref}
                            />
                        )}
                    />
                </Box>
            )}

            <Box pb="$M100" bpM100={{ pb: '$S500' }}>
                <Controller
                    control={control}
                    name="consent__insights__24"
                    rules={{ required: true }}
                    render={({ field: { onChange, name, ref, onBlur } }) => (
                        <EmailPreferencesToggle
                            legend="Email preferences"
                            label="I want to hear about the latest property news, advice and new home developments from Zoopla and its selected partners only."
                            errorText={
                                errors.consent__insights__24 &&
                                'Please set your email preferences.'
                            }
                            name={name}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                        />
                    )}
                />
            </Box>

            <Box pb="$M100">
                <ReassuranceText data-testid="unsubscribe-text">
                    You can unsubscribe or update your preferences at any time.
                </ReassuranceText>
            </Box>

            <Box pb="$S500" display="flex" justifyContent="center">
                <Box width="100%" bpM100={{ width: '50%' }}>
                    <Button
                        category="primary"
                        loading={isSubmitting}
                        loadingText="Saving"
                        data-testid="update-preferences-button"
                        fw
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default PreferencesForm;
